/**
 * Jumpstart Theme - Main JavaScript Entry Point
 *
 * @package Jumpstart
 */

// Import styles (processed by Webpack)
import '../scss/main.scss';

// Import AOS (Animate On Scroll)
import AOS from 'aos';
import 'aos/dist/aos.css';

// Import GLightbox
import GLightbox from 'glightbox';
import 'glightbox/dist/css/glightbox.min.css';

// Import Masonry + imagesLoaded
import Masonry from 'masonry-layout';
import imagesLoaded from 'imagesloaded';

// Import Bootstrap components (only what we need)
import Modal from 'bootstrap/js/dist/modal';
import Collapse from 'bootstrap/js/dist/collapse';
import Dropdown from 'bootstrap/js/dist/dropdown';
import Offcanvas from 'bootstrap/js/dist/offcanvas';
import Alert from 'bootstrap/js/dist/alert';
import Tab from 'bootstrap/js/dist/tab';
import Tooltip from 'bootstrap/js/dist/tooltip';

// Import Swiper
import Swiper from 'swiper';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';

// Import Calendar Widget
import './calendar-widget.js';

// Import Banner Rotation
import { initBannerRotation } from './banner-rotation.js';

// Import Events Scroller
import { initEventsScroller } from './events-scroller.js';

// Import Hero Background Video
import { initHeroVideoBackgrounds } from './hero-video.js';

/**
 * DOM Ready handler
 */
document.addEventListener('DOMContentLoaded', function() {
  initAOS();
  initHeader();
  initHeaderOffset();
  initMobileMenu();
  initTooltips();
  initBackToTop();
  initSmoothScroll();
  initHeroSlider();
  initHeroVideoBackgrounds();
  initImageCarousel();
  initTestimonialsSlider();
  initEventsScroller();
  initBannerRotation();
  initEventsWidget();
  initHeadingIcons();
  initGalleryLightbox();
  initMasonryGallery();
});

/**
 * Initialize AOS (Animate On Scroll)
 */
function initAOS() {
  AOS.init({
    duration: 800,
    easing: 'ease-in-out',
    once: true,
    offset: 100,
  });
}

/**
 * Header scroll behavior
 */
function initHeader() {
  const header = document.querySelector('.site-header');
  if (!header) return;

  const scrollThresholdOn = 100;
  const scrollThresholdOff = 80;
  // A requested state must hold steady for this long before it's actually
  // applied to the DOM. Rapid re-triggering near the threshold (from any
  // cause) keeps cancelling/rescheduling this timer instead of touching the
  // class, so the class can never flicker faster than this interval.
  const debounceDelay = 150;

  let appliedState = window.scrollY > scrollThresholdOn;
  let pendingState = appliedState;
  let debounceTimer = null;

  header.classList.toggle('is-scrolled', appliedState);

  function requestState(nextState) {
    if (nextState === pendingState) return; // already requested/scheduled — let the pending timer run
    pendingState = nextState;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function() {
      if (pendingState !== appliedState) {
        appliedState = pendingState;
        header.classList.toggle('is-scrolled', appliedState);
      }
    }, debounceDelay);
  }

  function handleScroll() {
    if (window.scrollY > scrollThresholdOn) {
      requestState(true);
    } else if (window.scrollY < scrollThresholdOff) {
      requestState(false);
    }
  }

  // Throttled scroll listener
  let ticking = false;
  window.addEventListener('scroll', function() {
    if (!ticking) {
      window.requestAnimationFrame(function() {
        handleScroll();
        ticking = false;
      });
      ticking = true;
    }
  });
}

/**
 * Keep --header-offset in sync with the fixed header's real rendered height.
 * A ResizeObserver covers every case that changes the header's box size:
 * the is-scrolled condensed state, viewport/breakpoint changes, and web-font
 * load reflow. header.php also sets this synchronously via an inline
 * <script> right after the header markup so there's no flash before this
 * bundle loads/executes.
 */
function initHeaderOffset() {
  const header = document.querySelector('.site-header');
  if (!header) return;

  function setOffset() {
    document.documentElement.style.setProperty('--header-offset', header.offsetHeight + 'px');
  }

  //setOffset();

  // While the header's CSS transition (padding/box-shadow on .is-scrolled,
  // see $transition-base) is animating, poll via rAF instead of relying on
  // ResizeObserver alone — browsers can silently stop delivering
  // ResizeObserver notifications partway through a fast multi-frame CSS
  // transition, which would leave --header-offset stuck at a mid-animation
  // value and content misaligned with the header.
  let rafId = null;
  let stopAt = 0;

  function syncDuringTransition() {
    setOffset();
    if (performance.now() < stopAt) {
      rafId = requestAnimationFrame(syncDuringTransition);
    } else {
      rafId = null;
    }
  }

  function startSync() {
    stopAt = performance.now() + 400; // $transition-base (0.3s) + margin
    if (!rafId) {
      rafId = requestAnimationFrame(syncDuringTransition);
    }
  }

  const classObserver = new MutationObserver(startSync);
  classObserver.observe(header, { attributes: true, attributeFilter: ['class'] });

  // Fallback for viewport resize / breakpoint changes / web-font load
  // reflow — one-off size changes with no transition to poll through.
  if (window.ResizeObserver) {
    new ResizeObserver(setOffset).observe(header);
  } else {
    window.addEventListener('resize', setOffset);
  }
}

/**
 * Mobile menu functionality
 */
function initMobileMenu() {
  const menuToggle = document.querySelector('.menu-toggle');
  const mobileNav = document.querySelector('.mobile-nav');

  if (!menuToggle || !mobileNav) return;

  const offcanvasInstance = Offcanvas.getOrCreateInstance(mobileNav);

  // Give each parent item its own submenu-toggle button, separate from the
  // link, so items with a real URL stay clickable while the submenu opener
  // still lives where the arrow icon is.
  const menuItemsWithChildren = mobileNav.querySelectorAll('.menu-item-has-children');

  menuItemsWithChildren.forEach(function(item) {
    const link = item.querySelector(':scope > a');
    if (!link) return;

    // Placeholder links ("#") have nothing to navigate to - only the toggle
    // button should act on them.
    const href = link.getAttribute('href');
    if (!href || href === '#') {
      link.addEventListener('click', function(e) {
        e.preventDefault();
      });
    }

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'submenu-toggle';
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', 'Toggle submenu');
    toggle.innerHTML = '<i class="fa-solid fa-angle-right" aria-hidden="true"></i>';

    const icon = toggle.querySelector('i');

    toggle.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      const isOpen = item.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', String(isOpen));
      icon.classList.toggle('fa-angle-right', !isOpen);
      icon.classList.toggle('fa-angle-down', isOpen);
    });

    link.insertAdjacentElement('afterend', toggle);
  });

  // Close mobile menu when a link that actually navigates is clicked.
  const menuLinks = mobileNav.querySelectorAll('a');

  menuLinks.forEach(function(link) {
    const href = link.getAttribute('href');
    if (!href || href === '#') return;

    link.addEventListener('click', function() {
      offcanvasInstance.hide();
    });
  });
}

/**
 * Initialize Bootstrap tooltips
 */
function initTooltips() {
  const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
  tooltipTriggerList.forEach(function(tooltipTriggerEl) {
    new Tooltip(tooltipTriggerEl);
  });
}

/**
 * Back to top button
 */
function initBackToTop() {
  const backToTopBtn = document.querySelector('.back-to-top');
  if (!backToTopBtn) return;

  const showThreshold = 300;

  function toggleButton() {
    if (window.scrollY > showThreshold) {
      backToTopBtn.classList.add('is-visible');
    } else {
      backToTopBtn.classList.remove('is-visible');
    }
  }

  // Initial check
  toggleButton();

  // Throttled scroll listener
  let ticking = false;
  window.addEventListener('scroll', function() {
    if (!ticking) {
      window.requestAnimationFrame(function() {
        toggleButton();
        ticking = false;
      });
      ticking = true;
    }
  });

  // Scroll to top on click
  backToTopBtn.addEventListener('click', function(e) {
    e.preventDefault();
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  });
}

/**
 * Smooth scroll for anchor links
 */
function initSmoothScroll() {
  const anchorLinks = document.querySelectorAll('a[href^="#"]:not([href="#"])');

  anchorLinks.forEach(function(link) {
    link.addEventListener('click', function(e) {
      const targetId = this.getAttribute('href');
      const targetElement = document.querySelector(targetId);

      if (targetElement) {
        e.preventDefault();

        // Get header height for offset
        const header = document.querySelector('.site-header');
        const headerHeight = header ? header.offsetHeight : 0;

        const targetPosition = targetElement.getBoundingClientRect().top + window.scrollY - headerHeight;

        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });

        // Update URL hash without jumping
        history.pushState(null, null, targetId);
      }
    });
  });
}

/**
 * Initialize Hero Slider
 */
function initHeroSlider() {
  const sliders = document.querySelectorAll('.hero-slider');

  sliders.forEach(function(slider) {
    new Swiper(slider, {
      modules: [Navigation, Pagination, Autoplay],
      slidesPerView: 1,
      loop: true,
      effect: 'fade',
      autoplay: {
        delay: 7000,
        disableOnInteraction: false,
      },
      navigation: {
        nextEl: slider.querySelector('.hero-slider__next'),
        prevEl: slider.querySelector('.hero-slider__prev'),
      },
      pagination: {
        el: slider.querySelector('.hero-slider__pagination'),
        type: 'fraction',
      },
    });
  });
}

/**
 * Initialize Image Carousel sliders
 */
function initImageCarousel() {
  const carousels = document.querySelectorAll('.image-carousel-slider');

  carousels.forEach(function(carousel) {
    new Swiper(carousel, {
      modules: [Autoplay, Navigation],
      slidesPerView: 2,
      spaceBetween: 30,
      loop: true,
      autoplay: {
        delay: 3000,
        disableOnInteraction: false,
      },
      navigation: {
        nextEl: carousel.querySelector('.swiper-button-next'),
        prevEl: carousel.querySelector('.swiper-button-prev'),
      },
      breakpoints: {
        576: {
          slidesPerView: 3,
        },
        768: {
          slidesPerView: 4,
        },
        992: {
          slidesPerView: 5,
        },
        1200: {
          slidesPerView: 6,
        },
      },
    });
  });
}

/**
 * Initialize Testimonials Slider
 */
function initTestimonialsSlider() {
  const sliders = document.querySelectorAll('.testimonials-slider');

  sliders.forEach(function(slider) {
    new Swiper(slider, {
      modules: [Navigation],
      slidesPerView: 1,
      effect: 'fade',
      //spaceBetween: 30,
      loop: true,
      navigation: {
        nextEl: slider.querySelector('.testimonials-slider__next'),
        prevEl: slider.querySelector('.testimonials-slider__prev'),
      },
    });
  });
}

// Events scroller now handled by imported module (events-scroller.js)
// Old functions removed - functionality replaced with EventsScroller class

/**
 * Enhance Events Widget
 * Adds event titles and register buttons from hidden track data
 */
function initEventsWidget() {
  // Wait a bit for widget to load, then enhance
  setTimeout(function() {
    const sponsors = document.querySelectorAll('.events__featured-widget-wrapper .sponsor');
    const tracks = document.querySelectorAll('.events__featured-widget-wrapper .mni-sponsors-track');

    if (sponsors.length > 0 && tracks.length > 0) {
      enhanceWidget(sponsors, tracks);
    } else {
      // Try again after longer delay
      setTimeout(function() {
        const sponsorsRetry = document.querySelectorAll('.events__featured-widget-wrapper .sponsor');
        const tracksRetry = document.querySelectorAll('.events__featured-widget-wrapper .mni-sponsors-track');

        if (sponsorsRetry.length > 0 && tracksRetry.length > 0) {
          enhanceWidget(sponsorsRetry, tracksRetry);
        }
      }, 2000);
    }
  }, 1000);
}

/**
 * Enhance widget with titles and buttons
 */
function enhanceWidget(sponsors, tracks) {
  sponsors.forEach(function(sponsor, index) {
    // Get corresponding track with title
    const track = tracks[index];
    if (!track) return;

    const eventTitle = track.getAttribute('title');
    if (!eventTitle) return;

    // Find the date div (second child div inside the link's first div)
    const link = sponsor.querySelector('a');
    if (!link) return;

    const innerDiv = link.querySelector('div');
    if (!innerDiv) return;

    const dateDiv = innerDiv.querySelector('div:last-child');
    if (!dateDiv) return;

    // Create content wrapper
    const contentWrapper = document.createElement('div');
    contentWrapper.className = 'events-widget__content';

    // Move date into content wrapper
    const dateText = dateDiv.textContent;
    dateDiv.remove();

    // Create date element
    const dateElement = document.createElement('div');
    dateElement.className = 'events-widget__date';
    dateElement.textContent = dateText;

    // Create title element
    const titleElement = document.createElement('h4');
    titleElement.className = 'events-widget__title';
    titleElement.textContent = eventTitle;

    // Create button
    const buttonElement = document.createElement('span');
    buttonElement.className = 'events-widget__button btn btn-vibrant btn-sm';
    buttonElement.textContent = 'Register Now';

    // Append elements to content wrapper
    contentWrapper.appendChild(dateElement);
    contentWrapper.appendChild(titleElement);
    contentWrapper.appendChild(buttonElement);

    // Append content wrapper to inner div
    innerDiv.appendChild(contentWrapper);
  });
}

/**
 * Initialize Swiper sliders
 * Uncomment and configure as needed
 */
// function initSwipers() {
//   // Hero slider example
//   const heroSlider = document.querySelector('.hero-slider');
//   if (heroSlider) {
//     new Swiper(heroSlider, {
//       modules: [Navigation, Pagination, Autoplay],
//       slidesPerView: 1,
//       spaceBetween: 0,
//       loop: true,
//       autoplay: {
//         delay: 5000,
//         disableOnInteraction: false,
//       },
//       pagination: {
//         el: '.swiper-pagination',
//         clickable: true,
//       },
//       navigation: {
//         nextEl: '.swiper-button-next',
//         prevEl: '.swiper-button-prev',
//       },
//     });
//   }
//
//   // Testimonials slider example
//   const testimonialsSlider = document.querySelector('.testimonials-slider');
//   if (testimonialsSlider) {
//     new Swiper(testimonialsSlider, {
//       modules: [Navigation, Pagination],
//       slidesPerView: 1,
//       spaceBetween: 30,
//       loop: true,
//       pagination: {
//         el: '.swiper-pagination',
//         clickable: true,
//       },
//       breakpoints: {
//         768: {
//           slidesPerView: 2,
//         },
//         1024: {
//           slidesPerView: 3,
//         },
//       },
//     });
//   }
// }

/**
 * Spin heading icons when their wrapper enters the viewport via AOS.
 * Handles any block that uses the js-heading-icons class on its heading wrap.
 */
function initHeadingIcons() {
  document.addEventListener('aos:in', function({ detail }) {
    if (!detail.classList.contains('js-heading-icons')) return;

    detail.querySelectorAll('.js-heading-icon').forEach(function(icon) {
      icon.classList.remove('is-spinning');
      void icon.offsetWidth; // force reflow to restart keyframe
      icon.classList.add('is-spinning');
    });
  });
}

function initGalleryLightbox() {
  if ( document.querySelector('.glightbox') ) {
    GLightbox({
      selector: '.glightbox',
      touchNavigation: true,
      loop: true,
      autoplayVideos: false,
    });
  }
}

function initMasonryGallery() {
  document.querySelectorAll('[data-masonry-gallery]').forEach(function(grid) {
    imagesLoaded(grid, function() {
      new Masonry(grid, {
        itemSelector: '.gallery-masonry-item',
        percentPosition: true,
        gutter: 20,
      });
    });
  });
}

/**
 * Utility: Debounce function
 */
function debounce(func, wait = 100) {
  let timeout;
  return function(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

/**
 * Utility: Throttle function
 */
function throttle(func, limit = 100) {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/**
 * Expose utilities globally if needed
 */
window.Jumpstart = {
  debounce,
  throttle,
};


(function ($) {
  $(document).ready(function () {
    $('[data-accordion="init"]').each(function () {
      var $wrapper  = $(this);
      var headerSel = $wrapper.attr('data-accordion-header') || '.acc-header';

      $wrapper.find(headerSel).on('click', function () {
        var $header  = $(this);
        var $content = $header.next('.accordion-block__content');
        var isOpen   = $header.hasClass('accordion-header-active');

        $header.toggleClass('accordion-header-active', !isOpen);
        if (isOpen) {
          $content.slideUp(200, function () {
            // Reset embedded videos so playback stops when the answer collapses
            $content.find('iframe').each(function () {
              this.src = this.src;
            });
          });
        } else {
          $content.slideDown(200);
        }
      });
    });
  });
})(jQuery);